/** @jsx jsx */
import React, { useState } from "react"
import { jsx } from "theme-ui"
import Window from "./window"

interface ProjectsWindowProps {
  isOpen: boolean
  onClose: () => void
  onMinimize?: () => void
  zIndex?: number
  soundEnabled?: boolean
}

interface Project {
  id: string
  name: string
  filename: string
  description: string
  modified: string
  size: string
  tech: string[]
  demoUrl?: string
  sourceUrl?: string
  status?: string
}

const projects: Project[] = [
  {
    id: "grantsnotes",
    name: "GrantsNotes",
    filename: "GrantsNotes.exe",
    description:
      "A collaborative note-taking app built with React, Material UI and a Slate rich-text editor, originally backed by Firebase auth and MongoDB. Revived as a self-contained demo that runs in the browser.",
    modified: "2026-10-17",
    size: "2.4 MB",
    tech: ["React", "Material-UI", "Slate", "localStorage"],
    demoUrl: "/grantsnotes/",
    sourceUrl: "https://github.com/calec/notes-app",
  },
  {
    id: "hero-association",
    name: "Hero Association",
    filename: "HeroAssociation.exe",
    description:
      "A cross-platform fitness tracker for the 100 push-ups, 100 sit-ups, 100 squats and 10 km daily challenge, with S-Class and C-Class streaks and live leaderboards. Built with Expo and React Native Web on Supabase; the demo runs entirely in the browser with a seeded roster of rival heroes.",
    modified: "2026-10-18",
    size: "6.2 MB",
    tech: ["Expo", "React Native", "TypeScript", "Supabase"],
    demoUrl: "/hero-association/",
  },
]

const ProjectsWindow = ({ isOpen, onClose, onMinimize, zIndex, soundEnabled }: ProjectsWindowProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <Window
      title="C:\Projects"
      icon="📁"
      defaultPosition={{ x: 150, y: 80 }}
      defaultSize={{ width: 680, height: 500 }}
      isOpen={isOpen}
      onClose={onClose}
      onMinimize={onMinimize}
      zIndex={zIndex}
      soundEnabled={soundEnabled}
    >
      {/* Address Bar / Breadcrumb Toolbar */}
      <div
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          mb: "16px",
          pb: "12px",
          borderBottom: "1px solid rgba(var(--retro-primary-rgb), 0.2)",
        }}
      >
        <span
          sx={{
            color: "#888",
            fontSize: "11px",
            flexShrink: 0,
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          Address:
        </span>
        <div
          sx={{
            flex: 1,
            background: "#0a0a0a",
            border: "1px solid rgba(var(--retro-primary-rgb), 0.4)",
            px: "10px",
            py: "4px",
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", monospace',
            fontSize: "12px",
            color: "var(--retro-primary)",
            letterSpacing: "0.02em",
          }}
        >
          C:\Users\Cale\Projects\
        </div>
        <span
          sx={{
            color: "#888",
            fontSize: "10px",
            flexShrink: 0,
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          [{projects.length} items]
        </span>
      </div>

      {/* Column Headers */}
      <div
        sx={{
          display: "grid",
          // Mobile: name only; tablet+: full columns
          gridTemplateColumns: ["1fr", "1fr", "1fr 120px 80px"],
          gap: "8px",
          px: "8px",
          pb: "6px",
          mb: "4px",
          borderBottom: "1px solid rgba(var(--retro-primary-rgb), 0.15)",
          color: "#888",
          fontSize: "10px",
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        <span>Name</span>
        <span sx={{ display: ["none", "none", "block"] }}>Modified</span>
        <span sx={{ display: ["none", "none", "block"] }}>Size</span>
      </div>

      {/* Project File Entries */}
      <div
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        {projects.map((project) => {
          const isExpanded = expandedId === project.id

          return (
            <div
              key={project.id}
              sx={{
                border: isExpanded
                  ? "1px solid rgba(var(--retro-primary-rgb), 0.5)"
                  : "1px solid rgba(var(--retro-primary-rgb), 0.15)",
                background: isExpanded ? "rgba(var(--retro-primary-rgb), 0.04)" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  border: "1px solid rgba(var(--retro-primary-rgb), 0.4)",
                  background: "rgba(var(--retro-primary-rgb), 0.03)",
                },
              }}
            >
              {/* File Row (always visible) */}
              <div
                onClick={() => toggleExpand(project.id)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: ["1fr", "1fr", "1fr 120px 80px"],
                  gap: "8px",
                  px: "8px",
                  py: "8px",
                  cursor: "pointer",
                  alignItems: "center",
                }}
              >
                {/* Name column */}
                <div
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    overflow: "hidden",
                    justifyContent: "space-between",
                  }}
                >
                  <span sx={{ fontSize: "14px", flexShrink: 0 }}>📄</span>
                  <span
                    sx={{
                      color: "var(--retro-primary)",
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: "12px",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textShadow: "0 0 4px rgba(var(--retro-primary-rgb), 0.4)",
                    }}
                  >
                    {project.filename}
                  </span>
                  {project.status && (
                    <span
                      sx={{
                        color: "#ffb000",
                        fontSize: "9px",
                        border: "1px solid rgba(255, 176, 0, 0.5)",
                        px: "4px",
                        py: "1px",
                        flexShrink: 0,
                        fontFamily: '"JetBrains Mono", monospace',
                        letterSpacing: "0.04em",
                      }}
                    >
                      {project.status}
                    </span>
                  )}
                  {/* Expand arrow visible only on mobile */}
                  <span
                    sx={{
                      display: ["block", "block", "none"],
                      color: "var(--retro-primary)",
                      fontSize: "10px",
                      fontFamily: '"JetBrains Mono", monospace',
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      flexShrink: 0,
                      ml: "auto",
                    }}
                  >
                    {">"}
                  </span>
                </div>

                {/* Modified column — hidden on mobile */}
                <span
                  sx={{
                    display: ["none", "none", "block"],
                    color: "#888",
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: "11px",
                  }}
                >
                  {project.modified}
                </span>

                {/* Size column + expand arrow — hidden on mobile; arrow shown in name col instead */}
                <div
                  sx={{
                    display: ["none", "none", "flex"],
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    sx={{
                      color: "#888",
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: "11px",
                    }}
                  >
                    {project.size}
                  </span>
                  <span
                    sx={{
                      color: "var(--retro-primary)",
                      fontSize: "10px",
                      fontFamily: '"JetBrains Mono", monospace',
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      flexShrink: 0,
                    }}
                  >
                    {">"}
                  </span>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div
                  sx={{
                    px: "16px",
                    pb: "14px",
                    pt: "4px",
                    borderTop: "1px solid rgba(var(--retro-primary-rgb), 0.15)",
                  }}
                >
                  {/* Description */}
                  <p
                    sx={{
                      color: "#888",
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: "11px",
                      lineHeight: 1.7,
                      mb: "12px",
                      mt: "10px",
                    }}
                  >
                    {project.description}
                  </p>

                  {/* Tech Stack Badges */}
                  <div
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      mb: "12px",
                    }}
                  >
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: "10px",
                          color: "var(--retro-primary)",
                          border: "1px solid var(--retro-primary)",
                          px: "6px",
                          py: "2px",
                          letterSpacing: "0.04em",
                        }}
                      >
                        [{tech}]
                      </span>
                    ))}
                  </div>

                  {/* Metadata row */}
                  <div
                    sx={{
                      display: "flex",
                      gap: "20px",
                      mb: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      sx={{
                        color: "#888",
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: "10px",
                      }}
                    >
                      Modified:{" "}
                      <span sx={{ color: "#ffb000" }}>{project.modified}</span>
                    </span>
                    <span
                      sx={{
                        color: "#888",
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: "10px",
                      }}
                    >
                      Size: <span sx={{ color: "#ffb000" }}>{project.size}</span>
                    </span>
                  </div>

                  {/* Links */}
                  <div
                    sx={{
                      display: "flex",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    {project.demoUrl && (
                      <a
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "var(--retro-primary)",
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: "11px",
                          textDecoration: "none",
                          letterSpacing: "0.04em",
                          "&:hover": {
                            textDecoration: "underline",
                            textShadow: "0 0 6px rgba(var(--retro-primary-rgb), 0.6)",
                          },
                        }}
                      >
                        {">"} open demo
                      </a>
                    )}
                    {project.sourceUrl && (
                      <a
                        href={project.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "var(--retro-primary)",
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: "11px",
                          textDecoration: "none",
                          letterSpacing: "0.04em",
                          "&:hover": {
                            textDecoration: "underline",
                            textShadow: "0 0 6px rgba(var(--retro-primary-rgb), 0.6)",
                          },
                        }}
                      >
                        {">"} view source
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Status Bar */}
      <div
        sx={{
          mt: "16px",
          pt: "10px",
          borderTop: "1px solid rgba(var(--retro-primary-rgb), 0.2)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          sx={{
            color: "#888",
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: "10px",
          }}
        >
          {projects.length} object(s)
        </span>
        <span
          sx={{
            color: "#888",
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: "10px",
          }}
        >
          {expandedId
            ? `Selected: ${projects.find((p) => p.id === expandedId)?.filename ?? ""}`
            : "Click a file to expand"}
        </span>
      </div>
    </Window>
  )
}

export default ProjectsWindow
